import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home", id: "home" },
  { to: "/our-story", label: "Our Story", id: "our-story" },
  { to: "/the-problem", label: "Problem", id: "the-problem" },
  { to: "/our-solution", label: "Solution", id: "our-solution" },
  { to: "/technology", label: "Technology", id: "technology" },
  { to: "/for-business", label: "For Business", id: "for-business" },
  { to: "/sustainability", label: "Sustainability", id: "sustainability" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <header
        data-testid="navbar"
        className="fixed top-0 inset-x-0 z-50 border-b border-forest/10 bg-cream/80 backdrop-blur-md"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 h-16 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group">
            <img
              src="/fresh-guard-logo.png"
              alt="Fresh Guard logo"
              data-testid="nav-logo-mark"
              className="h-9 w-9 rounded-full bg-white object-cover ring-1 ring-forest/10 transition-transform group-hover:scale-105"
            />
            <span className="font-display font-bold text-lg tracking-tight text-forest">
              Fresh Guard
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-6">
            {LINKS.map((l) => (
              <NavLink
                key={l.id}
                to={l.to}
                data-testid={`nav-link-${l.id}`}
                className={({ isActive }) =>
                  `relative font-mono text-[10px] uppercase tracking-[0.18em] transition-colors hover:text-forest ${
                    isActive ? "text-forest" : "text-fog"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {l.label}
                    {isActive && (
                      <motion.span
                        layoutId="nav-dot"
                        className="absolute -bottom-2 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full bg-amber"
                      />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/contact"
              data-testid="nav-cta-contact"
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-forest text-cream px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors hover:bg-emerald"
            >
              Start a conversation
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen(true)}
              className="lg:hidden h-10 w-10 flex items-center justify-center text-forest"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[80] bg-forest text-cream flex flex-col"
          >
            <div className="flex items-center justify-between px-6 h-16 border-b border-cream/10">
              <span className="flex items-center gap-2.5">
                <img
                  src="/fresh-guard-logo.png"
                  alt="Fresh Guard logo"
                  data-testid="mobile-logo-mark"
                  className="h-8 w-8 rounded-full bg-white object-cover"
                />
                <span className="font-display font-bold text-lg">Fresh Guard</span>
              </span>
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                className="h-10 w-10 flex items-center justify-center"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-1">
              {[...LINKS, { to: "/contact", label: "Contact", id: "contact" }].map((l, i) => (
                <motion.div
                  key={l.id}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.05, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.id}`}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `block py-2 font-display font-extrabold text-4xl tracking-tight transition-colors ${
                        isActive ? "text-mint" : "text-cream/80 hover:text-cream"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <p className="px-8 pb-10 font-mono text-[10px] uppercase tracking-[0.25em] text-cream/50">
              Riyadh, Saudi Arabia — FoodTech
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
